import React from "react";
import PageShell from "@/components/PageShell";
import { FadeUp, RevealLines } from "@/components/Reveal";

const team = [
  {
    n: "01",
    name: "Dr. Marius Weber",
    role: "Chief Scientific Officer",
    bio:
      "20+ years in medicinal chemistry across Roche (Basel) and a Rhine-corridor biotech. Led three IND filings in CNS. PhD, ETH Zürich.",
  },
  {
    n: "02",
    name: "Dr. Karin Rohner",
    role: "Head of Preclinical Pharmacology",
    bio:
      "Formerly senior scientist at a Basel-Landschaft CRO. Specialist in monoamine-transporter pharmacology and rodent behavioural models.",
  },
  {
    n: "03",
    name: "Dr. Lorenzo Amstad",
    role: "Head of Process Chemistry",
    bio:
      "Continuous-flow chemistry expert. Previously scaled two commercial APIs. Co-inventor on 11 process patents.",
  },
  {
    n: "04",
    name: "Dr. Philippe Béguelin",
    role: "Head of ADME & DMPK",
    bio:
      "Trained at University of Basel Pharmazentrum. Focuses on permeability, microsomal stability and species-scaling in early candidates.",
  },
];

export default function About() {
  return (
    <PageShell title="About — NewGenPharma">
      <section className="px-6 md:px-10 pt-16 md:pt-24 pb-16">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="font-mono-tight text-[11px] text-ink/60 flex items-center gap-4 mb-8">
              <span className="w-1.5 h-1.5 bg-oxred inline-block" />
              §03 · About
            </div>
          </FadeUp>
          <RevealLines
            as="h1"
            className="font-display text-[13vw] md:text-[8vw] leading-[0.92] tracking-[-0.03em] max-w-[18ch]"
            lines={["Young group.", <><em className="italic text-oxred">Old</em> hands.</>]}
          />
        </div>
      </section>

      {/* SPOTLIGHT IMAGE */}
      <section className="px-6 md:px-10 pb-24">
        <div className="mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-8">
          <FadeUp className="md:col-span-8">
            <div className="relative aspect-[16/9] overflow-hidden border border-ink/15">
              <img
                src="https://images.unsplash.com/photo-1614595737476-42487331b8a1"
                alt="Swiss architecture"
                className="w-full h-full object-cover"
                style={{ filter: "contrast(1.02) saturate(0.7)" }}
              />
              <div className="absolute top-4 left-4 font-mono-tight text-[10px] text-bone bg-ink/70 px-2 py-1">
                HQ · BAARERSTRASSE 78 · CH-6300 ZUG
              </div>
            </div>
          </FadeUp>
          <FadeUp className="md:col-span-4" delay={0.2}>
            <div className="h-full flex flex-col justify-end">
              <p className="text-ink/80 text-lg leading-relaxed">
                NewGenPharma was incorporated in Zug in April 2023. Our founding team came together after
                two decades of parallel work at three Swiss and German pharmaceutical houses. We had shipped
                APIs, filed INDs, and — often — watched promising CNS programs die inside larger portfolios.
              </p>
              <p className="text-ink/80 text-lg leading-relaxed mt-6">
                We built NewGenPharma to be small, technical, and honest about what a young group can do:
                excellent chemistry, transparent documentation, and a tightly-focused therapeutic area we
                understand intimately.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="px-6 md:px-10 py-24 bg-bone-2/50 border-y border-ink/10">
        <div className="mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-8">
          <FadeUp className="md:col-span-4">
            <span className="font-mono-tight text-[11px] text-ink/60">§ Principles</span>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight mt-4 leading-[0.98]">
              How we work.
            </h2>
          </FadeUp>
          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/15 border border-ink/15">
            {[
              {
                t: "Focus over breadth",
                b: "One therapeutic area, four scaffolds, six programs. No portfolio noise.",
              },
              {
                t: "Documentation as product",
                b: "The paperwork is the deliverable. CoA, DMF, ICH residuals — non-negotiable.",
              },
              {
                t: "Small teams, senior only",
                b: "17 scientists, no juniors on lead chemistry. Every batch has a named responsible chemist.",
              },
              {
                t: "Partnered manufacturing",
                b: "GMP-aligned facility in Basel-Landschaft. Continuous-flow process where sensible.",
              },
            ].map((p) => (
              <FadeUp key={p.t}>
                <div className="bg-bone p-8 md:p-10 h-full">
                  <h3 className="font-display text-2xl tracking-tight">{p.t}</h3>
                  <p className="mt-4 text-ink/75 text-sm leading-relaxed">{p.b}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="px-6 md:px-10 py-32">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="flex items-baseline justify-between mb-16 border-b border-ink/15 pb-6">
              <h2 className="font-display text-4xl md:text-5xl tracking-tight">Founding team</h2>
              <span className="font-mono-tight text-[11px] text-ink/60">04 · Scientists</span>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
            {team.map((m) => (
              <FadeUp key={m.n}>
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-2 font-mono text-4xl md:text-5xl leading-none text-ink/80">
                    {m.n}
                  </div>
                  <div className="col-span-10">
                    <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.05]">{m.name}</h3>
                    <div className="font-mono-tight text-[11px] text-oxred mt-3">{m.role}</div>
                    <p className="mt-5 text-ink/75 leading-relaxed max-w-md">{m.bio}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="px-6 md:px-10 pb-24">
        <div className="mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-8">
          <FadeUp className="md:col-span-5">
            <span className="font-mono-tight text-[11px] text-ink/60">§ Location</span>
            <h3 className="font-display text-4xl md:text-6xl tracking-tight mt-6 leading-[0.98]">
              Zug &amp; <em className="italic text-oxred">Basel-Landschaft.</em>
            </h3>
            <p className="mt-8 text-ink/75 leading-relaxed max-w-md">
              Headquarters and administration in Zug, the traditional Swiss centre for pharmaceutical trade
              and holding groups. Chemistry and analytical laboratories in Basel-Landschaft, on the Rhine
              corridor — 25 minutes from Basel and its Q&amp;A ecosystem.
            </p>
          </FadeUp>
          <FadeUp className="md:col-span-7" delay={0.15}>
            <div className="relative aspect-[4/3] overflow-hidden border border-ink/15">
              <img
                src="https://images.unsplash.com/photo-1532094349884-543bc11b234d"
                alt="Laboratory glassware"
                className="w-full h-full object-cover"
                style={{ filter: "contrast(1.05) saturate(0.8)" }}
              />
              <div className="absolute bottom-4 left-4 font-mono-tight text-[10px] text-bone bg-ink/70 px-2 py-1">
                LAB · BASEL-LANDSCHAFT
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </PageShell>
  );
}
